import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { reviewsAPI } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Flag, Loader2, Star, Trash2, RotateCcw } from "lucide-react";

export default function AdminReportedReviews() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.is_admin || user?.is_super_admin;

  const load = () => {
    reviewsAPI.listReported()
      .then((r) => setReviews(r.data?.reviews || []))
      .catch(() => toast.error("Could not load"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [isAdmin]);

  const moderate = async (id, action) => {
    try { await reviewsAPI.moderate(id, action); toast.success(action === "remove" ? "Removed" : "Restored"); load(); }
    catch { toast.error("Could not update"); }
  };

  if (!isAdmin) return <div className="py-20 text-center text-muted-foreground">Admin access required.</div>;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <h1 className="flex items-center gap-2 font-display text-3xl font-bold"><Flag className="h-6 w-6 text-primary" /> Reported reviews</h1>

      {loading ? (
        <Loader2 className="mx-auto h-5 w-5 animate-spin" />
      ) : reviews.length === 0 ? (
        <p className="py-10 text-center text-sm text-muted-foreground">No reported reviews.</p>
      ) : (
        <div className="space-y-3" data-testid="reported-reviews-list">
          {reviews.map((r) => (
            <Card key={r.id} className="space-y-2 border-border bg-card p-4" data-testid={`reported-review-${r.id}`}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold">{r.buyer_username} → {r.seller_username}</p>
                <span className="flex items-center gap-1 text-xs"><Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {r.rating} · {r.review_kind}</span>
              </div>
              <p className="text-sm text-foreground/80">"{r.review_text}"</p>
              {r.report_reason && <p className="text-xs text-amber-500">Report reason: {r.report_reason}</p>}
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => moderate(r.id, "restore")} data-testid={`restore-${r.id}`}><RotateCcw className="mr-1 h-3.5 w-3.5" /> Keep (restore)</Button>
                <Button size="sm" variant="destructive" onClick={() => moderate(r.id, "remove")} data-testid={`remove-${r.id}`}><Trash2 className="mr-1 h-3.5 w-3.5" /> Remove</Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
